import {type ReactNode, useEffect, useMemo, useRef} from 'react';
import {toast} from 'sonner';

import {ButtonRaw} from '@/components/Button';
import useTheme from '@/hooks/useTheme';
import appContent from '@/services/content/appContent';
import {twx} from '@/utils/tailwindUtils';

import type {ToastAction} from './Toaster';

type ToastProps = {
    id: string | number;
    title?: ReactNode;
    description?: ReactNode;
    action?: ToastAction;
};

const Toast = ({id, title, description, action}: ToastProps) => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const {isThemeDark} = useTheme();

    const buttonAction = useMemo<ToastAction | undefined>(() => {
        if (action) {
            return action;
        }

        const globalButtons = appContent.getGlobalButtons();
        const dismissButton = globalButtons?.dismissButton;

        if (!dismissButton) {
            return undefined;
        }

        return {
            label: dismissButton.text,
        };
    }, [action]);

    const {label, onClick} = buttonAction || {};

    useEffect(() => {
        if (!buttonAction) {
            return;
        }

        buttonRef.current?.focus();
    }, [buttonAction]);

    const handleButtonClick = () => {
        onClick?.();
        toast.dismiss(id);
    };

    return (
        <div
            className={twx(
                'flex w-full flex-row justify-between gap-4 text-start text-14 tracking-04 text-romance',
                isThemeDark && 'text-primary-dark'
            )}
        >
            <div className='flex flex-1 flex-col gap-2'>
                {title && <div className='font-bold'>{title}</div>}
                {description && <div>{description}</div>}
            </div>
            {buttonAction && (
                // @ts-ignore
                <ButtonRaw
                    ref={buttonRef}
                    className={twx(
                        'flex-none bg-transparent text-14 font-bold tracking-02 text-tertiary outline-none focus:outline-none',
                        isThemeDark && 'text-primary-dark'
                    )}
                    onClick={handleButtonClick}
                >
                    {label}
                </ButtonRaw>
            )}
        </div>
    );
};

export default Toast;
